import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container, VStack, Heading, Text, Box, Badge, HStack,
  OrderedList, ListItem, UnorderedList, Divider, Button, Link, Code,
} from "@chakra-ui/react";
import { FiArrowLeft, FiArrowRight, FiExternalLink } from "react-icons/fi";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

const TalentLinkPlusLaunch: React.FC = () => {
  return (
    <>
      <NavbarWithCallToAction />
      <Container maxW="container.md" py={10}>
        <VStack spacing={6} align="stretch">
          {/* Back link */}
          <Link as={RouterLink} to="/updates" _hover={{ textDecoration: "none" }}>
            <Button variant="ghost" size="sm" leftIcon={<FiArrowLeft />} color="gray.500">
              All Updates
            </Button>
          </Link>

          {/* Header */}
          <VStack spacing={3} align="start">
            <HStack>
              <Badge colorScheme="purple">Recruitment</Badge>
              <Text fontSize="xs" color="gray.400">24 April 2026</Text>
            </HStack>
            <Heading size="lg" color="#2c5282">
              Talent Link Plus is live — Joe can send his first candidate today
            </Heading>
            <Text color="gray.600">
              Hi Hao, you now have a dedicated recruitment portal at <strong>talentlinkplus.com.au</strong>.
              One branded link per role, candidates fill it in under 10 minutes from their phone,
              you and I get an email + SMS the moment they submit, and the full pipeline lives at
              <Code mx={1}>/prospecting</Code> in the admin. Here's what's ready and how you + Joe can start testing.
            </Text>
          </VStack>

          <Divider />

          {/* What's New */}
          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>What's new</Heading>
            <UnorderedList spacing={2} pl={5}>
              <ListItem>
                <strong>A dedicated recruitment site</strong> at <Code>talentlinkplus.com.au</Code> — separate from Paradise Lakes admin,
                designed just for Joe's candidate workflow. Home page pitches the value to
                any employer, so you can point other Paradise Lakes sister sites / aged care clients at it too later.
              </ListItem>
              <ListItem>
                <strong>The Registered Nurse pre-screening form you sent</strong> is built and live. All 75 questions
                across 7 sections, exactly matching your PDF. AHPRA verification, clinical areas,
                shift leadership experience, the two reflective questions, consent checkboxes, CV upload — all there.
              </ListItem>
              <ListItem>
                <strong>Paradise Lakes branding on the candidate-facing page</strong> — green accent, your employer name
                at the top. Candidates see <em>your</em> brand, not Talent Link Plus's. The recruitment layer stays invisible.
              </ListItem>
              <ListItem>
                <strong>One URL per role, sent to many candidates</strong> — no per-candidate setup.
                Joe sends the same link to 10 RN candidates, each fills out their own answers,
                each creates their own pipeline record. No duplicates, no confusion.
              </ListItem>
              <ListItem>
                <strong>Email + SMS the instant a candidate submits</strong> — both you and Tom get notified,
                with a link to open that candidate's record straight away.
              </ListItem>
              <ListItem>
                <strong>Companies, Employees, and Shifts modules are all live</strong> on Talent Link Plus too,
                so when you shortlist an RN, you can convert them to an Employee attached to the Paradise Lakes
                company and roster them onto shifts, without leaving the site.
              </ListItem>
            </UnorderedList>
          </Box>

          <Divider />

          {/* How to Test */}
          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>How to test with Joe — 10 minutes</Heading>
            <OrderedList spacing={3} pl={4}>
              <ListItem>
                <strong>Share this single URL with Joe</strong>:<br/>
                <Code p={2} display="inline-block" my={2} bg="gray.100">
                  https://talentlinkplus.com.au/prospecting/form/rn-paradise-lakes-aged-care
                </Code>
                <br/>
                Tell him: "Send this to every RN applicant instead of asking them to email a CV."
              </ListItem>
              <ListItem>
                <strong>Open the link yourself in a private/incognito window first</strong> — no login needed.
                Confirm it looks right (Paradise Lakes green branding, 75-question form, CV drag-drop at the bottom).
              </ListItem>
              <ListItem>
                <strong>Fill it in as a test candidate</strong> — use a test first/last name + your email.
                Takes 5-10 min. Drag any PDF as a CV.
              </ListItem>
              <ListItem>
                <strong>Hit Submit</strong> — you'll get an email within 30 seconds; Tom gets one too.
              </ListItem>
              <ListItem>
                <strong>Log into admin</strong> at <Code>https://talentlinkplus.com.au/login</Code> —
                your test candidate appears under Prospecting → Candidates with stage "SCREENING_REPLIED" and the CV attached.
              </ListItem>
              <ListItem>
                <strong>Once you're happy</strong>, ask Joe to send the same link to his five existing RN leads.
                We'll have your full pipeline visible in one place within a week.
              </ListItem>
            </OrderedList>
          </Box>

          <Divider />

          {/* Try It Now */}
          <Box bg="green.50" p={5} borderRadius="md" border="1px solid" borderColor="green.200">
            <Heading size="md" color="#38a169" mb={3}>Try it now</Heading>
            <Text mb={3}>
              Open the candidate-facing form exactly as an applicant would see it.
              No login, no sign-up, no CV email back-and-forth.
            </Text>
            <Link
              href="https://talentlinkplus.com.au/prospecting/form/rn-paradise-lakes-aged-care"
              isExternal
              _hover={{ textDecoration: "none" }}
            >
              <Button colorScheme="green" size="md" rightIcon={<FiExternalLink />}>
                Open the RN form
              </Button>
            </Link>
          </Box>

          {/* What's next */}
          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>What's next</Heading>
            <Text mb={3}>
              After you've run one test submission and we've confirmed it all fires correctly, I can build:
            </Text>
            <UnorderedList spacing={2} pl={5}>
              <ListItem>A Personal Carer form (already live as a template — just needs your PC-specific questions if they differ from the RN ones).</ListItem>
              <ListItem>Maintenance / kitchen / administration forms — same pattern, copy + tweak questions.</ListItem>
              <ListItem>An AI chase workflow so candidates who stall mid-form get a nudge SMS.</ListItem>
              <ListItem>A kanban view of the pipeline for Joe to drag candidates between stages.</ListItem>
            </UnorderedList>
          </Box>

          {/* Sign off */}
          <Box>
            <Text>As always, reach out if anything's not quite right or if you'd like me to adjust the form questions. Happy to jump on a call once you've tried a submission.</Text>
            <Text mt={4}>
              Regards,<br />
              <strong>Dr Thomas Miller</strong><br />
              <Text as="em" color="gray.500">Business Builder</Text>
            </Text>
          </Box>
        </VStack>
      </Container>
      <FooterWithFourColumns />
    </>
  );
};

export default TalentLinkPlusLaunch;
